import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { getAccountDocuments } from '../endpoints/accountDocument.ts';
import {
  AccountDocumentSummaryItemSchema,
  type AccountDocumentSummaryItem,
  type ReviewState,
} from '../model.ts';

const ACCOUNT_DOCUMENT_QUERY_NAMESPACE = 'account-documents' as const;

// API 的 pageSize 上限為 200，超出會被 clamp
const MAX_PAGE_SIZE = 200;

const fetchAllAccountDocuments = async (reviewState: ReviewState) => {
  const firstPage = await getAccountDocuments({
    reviewState,
    page: 1,
    pageSize: MAX_PAGE_SIZE,
  });

  const restPageNumbers = Array.from(
    { length: Math.max(firstPage.extra.totalPage - 1, 0) },
    (_, index) => index + 2,
  );

  const restPages = await Promise.all(
    restPageNumbers.map((page) =>
      getAccountDocuments({ reviewState, page, pageSize: MAX_PAGE_SIZE }),
    ),
  );

  const pages = [firstPage, ...restPages];

  return pages.flatMap(({ data }) => data);
};

const selectValidDocuments = (items: unknown[]) => {
  const documents: AccountDocumentSummaryItem[] = [];
  let invalidCount = 0;
  for (const item of items) {
    const parsed = AccountDocumentSummaryItemSchema.safeParse(item);
    if (parsed.success) {
      documents.push(parsed.data);
    } else {
      invalidCount += 1;
      console.error(
        '[useAccountDocumentList] invalid item removed',
        item,
        parsed.error,
      );
    }
  }
  return { documents, invalidCount };
};

const useAccountDocumentList = (reviewState: ReviewState) => {
  const query = useQuery({
    queryKey: [ACCOUNT_DOCUMENT_QUERY_NAMESPACE, 'list', reviewState],
    queryFn: () => fetchAllAccountDocuments(reviewState),
    select: selectValidDocuments,
  });

  useEffect(() => {
    if (!query.data || query.data.invalidCount === 0) return;
    notifications.show({
      id: 'invalid-account-documents',
      title: '部分文件資料格式錯誤',
      message: `有 ${query.data.invalidCount} 筆文件因資料格式錯誤未顯示，請聯絡工程團隊（詳見 console）`,
      color: 'red',
      autoClose: false,
    });
  }, [query.data]);

  return query;
};

export { ACCOUNT_DOCUMENT_QUERY_NAMESPACE, useAccountDocumentList };
